import React, { useEffect, useMemo, useState } from "react";
import Classes from "./ViewProduct.module.css";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import backend from "../../../../../api";
import { useSelector } from "react-redux";
import { useSnackbar } from 'notistack';

const LockedCell = ({ children = "Upgrade to Unlock", onClick }) => (
  <div
    className={`${Classes.cellValue} ${Classes.cellLocked}`}
    role="cell"
    onClick={onClick}
    style={{ cursor: "pointer" }}
  >
    <img src="/assets/icons/lock.png" height={13} /> {children}
  </div>
);

export default function ViewProduct() {
  const { id: routeId } = useParams();
  const location = useLocation();
  const { user } = useSelector((s) => s.auth);
  const { enqueueSnackbar } = useSnackbar();
  const navigateTo = useNavigate();

  const incomingInternet =
    location?.state?.source === "internet" ? location.state.product : null;
  const isExternal = String(routeId || "").startsWith("ext:");

  const [product, setProduct] = useState(incomingInternet || null);
  const [company, setCompany] = useState(null);
  const [source, setSource] = useState(incomingInternet ? "internet" : "local");
  const [loading, setLoading] = useState(!incomingInternet);

  const sameCompany = useMemo(
    () =>
      !!(
        user?.companyId &&
        product?.companyId &&
        String(user.companyId) === String(product.companyId)
      ),
    [user?.companyId, product?.companyId]
  );
  const mustUpgrade = useMemo(
    () => !sameCompany && user?.subscription === "free",
    [sameCompany, user?.subscription]
  );

  // 🔗 Central navigation for upgrade CTAs
  const goToUpgrade = () => {
    if (!user || !user._id) {
      // not logged in – send to signup (or pricing, your call)
      enqueueSnackbar("Create an account to upgrade your plan.", {
        variant: "info",
      });
      navigateTo("/signup/customer", {
        state: { fromUpgrade: true },
      });
      return;
    }
    // logged in free user → billing/settings page
    navigateTo("/settings/billing");
  };

  function toSnapshot(prod, source) {
    const price = Number(prod?.price || prod?.__raw?.list_price || 0) || 0;
    const image =
      (Array.isArray(prod?.images) && prod.images[0]) ||
      prod?.image ||
      (prod?.__raw?.images && prod.__raw.images[0]) ||
      prod?.__raw?.image_url ||
      "/assets/images/placeholder.png";

    const specs = [
      prod?.usage && `Usage: ${prod.usage}`,
      prod?.dimmable && `Dimmable: ${prod.dimmable}`,
      prod?.temperature && `Temp: ${prod.temperature}`,
      prod?.beamAngle && `Beam: ${prod.beamAngle}`,
      prod?.wattage && `Watt: ${prod.wattage}`,
    ].filter(Boolean);

    return {
      id:
        source === "internet"
          ? prod?.id ||
            prod?.__raw?.barcode_number ||
            prod?.__raw?.asin ||
            prod?.__raw?.mpn ||
            "ext_item"
          : prod?._id || prod?.id,
      name:
        prod?.productName ||
        prod?.name ||
        prod?.__raw?.title ||
        "Product",
      subtitle:
        prod?.description || prod?.subtitle || prod?.__raw?.category || "",
      brand: prod?.brand || prod?.__raw?.manufacturer || "",
      image,
      price,
      category: prod?.category || prod?.usage || prod?.__raw?.category || "",
      color: prod?.color || prod?.__raw?.color || "",
      specs,
      available: !!prod?.available || true,
    };
  }

  const handleAddToCollection = async () => {
    try {
      const companyId = user?.companyId;
      const ownerUserId = user?._id;
      if (!companyId || !ownerUserId) {
        enqueueSnackbar("Sign in required", { variant: "warning" });
        return;
      }
      const snapshot = toSnapshot(product, source);
      const productId =
        source === "internet"
          ? snapshot.id?.startsWith("ext:")
            ? snapshot.id
            : `ext:${snapshot.id}`
          : product?._id || product?.id;

      const payload = {
        companyId,
        ownerUserId,
        name: "My Collection",
        item: {
          source,
          productId,
          snapshot,
        },
      };

      const res = await backend.post("/api/collection/add", payload);
      if (res?.data?.data?._id) {
        enqueueSnackbar("Added to collection", { variant: "success" });
      } else {
        enqueueSnackbar("Failed to add", { variant: "error" });
      }
    } catch (e) {
      console.warn("add to collection error:", e?.response?.data || e.message);
      enqueueSnackbar("Error adding to collection", { variant: "error" });
    }
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (incomingInternet) return;
      setLoading(true);
      try {
        if (isExternal) {
          const token = String(routeId).slice(4);
          const res = await backend.get(`/api/ext/barcode`, {
            params: { search: token, page: 1 },
          });
          const data = res?.data || {};
          const it =
            Array.isArray(data?.products) && data.products.length
              ? data.products[0]
              : null;

          if (it && !cancelled) {
            const firstImage =
              (Array.isArray(it.images) &&
                it.images.length &&
                it.images[0]) ||
              it.image_url ||
              null;

            const rawStorePrice =
              Array.isArray(it.stores) &&
              it.stores.find((s) => s && s.price)?.price;

            const price = rawStorePrice
              ? Number(String(rawStorePrice).replace(/[^\d.]/g, ""))
              : Number(it.list_price || 0) || 0;

            const mapped = {
              productName:
                it.product_name || it.title || "Lighting Product",
              description: it.description || it.category || "",
              brand: it.brand || it.manufacturer || "",
              image: firstImage,
              images:
                (Array.isArray(it.images) &&
                  it.images.length &&
                  it.images) ||
                (firstImage ? [firstImage] : []),
              usage: it.category || "",
              dimmable: "",
              temperature: "",
              beamAngle: "",
              size: it.size || "",
              wattage: "",
              color: it.color || "",
              price,
              files: {},
              __internet: true,
              __raw: it,
            };
            setSource("internet");
            setProduct(mapped);
          } else if (!cancelled) {
            setSource("internet");
            setProduct(null);
          }
        } else {
          const res = await backend.get(`/api/products/${routeId}`);
          const p = res?.data?.data;
          if (!cancelled) {
            setProduct(p || null);
            setSource("local");
            if (p?.companyId) {
              const cmp = await backend.get(
                `/api/onboarding/${p.companyId}`
              );
              if (!cancelled) setCompany(cmp?.data?.data || null);
            }
          }
        }
      } catch (e) {
        console.warn("load product error:", e?.response?.data || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => (cancelled = true);
  }, [routeId, isExternal, incomingInternet]);

  // ensure manufacturer for local items if needed
  useEffect(() => {
    let cancelled = false;
    if (source === "local" && product?.companyId && !company) {
      (async () => {
        try {
          const cmp = await backend.get(
            `/api/onboarding/${product.companyId}`
          );
          if (!cancelled) setCompany(cmp?.data?.data || null);
        } catch {}
      })();
    }
    return () => (cancelled = true);
  }, [source, product?.companyId, company]);

  const gallery = useMemo(() => {
    const imgs =
      Array.isArray(product?.images) && product.images.length
        ? product.images
        : [product?.image].filter(Boolean);
    return imgs?.length
      ? imgs
      : [
          "https://fastly.picsum.photos/id/1005/600/400.jpg?hmac=RHLkAWAPjK7zvSVfmz7ULItYJMQQFF9kQ2SheLO5EV8",
        ];
  }, [product]);

  const [mainIdx, setMainIdx] = useState(0);

  const TABS =
    source === "internet"
      ? ["Specifications", "Manufacturer Info"]
      : ["Specifications", "Manufacturer Info", "Installation Guide"];

  const [activeTab, setActiveTab] = useState(TABS[0]);

  const lockOrVal = (val) =>
    mustUpgrade && source === "local" ? (
      <LockedCell onClick={goToUpgrade} />
    ) : (
      <div className={Classes.cellValue} role="cell">
        {val || "—"}
      </div>
    );

  const SpecRow = ({ label, value, lockable = false }) => {
    const val = value ?? "";
    if (source === "internet" && !String(val).trim()) return null;
    return (
      <div className={Classes.row} role="row">
        <div className={Classes.cellLabel} role="cell">
          {label}
        </div>
        {lockable ? (
          lockOrVal(val)
        ) : (
          <div className={Classes.cellValue} role="cell">
            {val || "—"}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <section className={`${Classes.wrapper} layoutPadding`}>
        <div className={Classes.loading}>Loading…</div>
      </section>
    );
  }

  if (!product) {
    return (
      <section className={`${Classes.wrapper} layoutPadding`}>
        <div className={Classes.loading}>Product not found.</div>
      </section>
    );
  }

  return (
    <section className={`${Classes.wrapper} layoutPadding`}>
      {/* Breadcrumbs */}
      <nav className={Classes.breadcrumbs} aria-label="Breadcrumb">
        <ol>
          <li>
            <a href="#">Home</a>
          </li>
          <li>
            <span>›</span>
          </li>
          <li>
            <a href="#">Products</a>
          </li>
          <li>
            <span>›</span>
          </li>
          <li aria-current="page">Product Details</li>
        </ol>
      </nav>

      <div className={Classes.grid}>
        {/* LEFT — GALLERY */}
        <div className={Classes.mediaCol}>
          <figure
            className={Classes.mainFigure}
            style={{ position: "relative" }}
          >
            {source === "internet" && (
              <span
                className={Classes.sourcePill}
                title="Fetched from Internet"
              >
                Internet
              </span>
            )}
            <img
              src={gallery[mainIdx]}
              alt="Product preview"
              className={Classes.mainImg}
              loading="eager"
              decoding="async"
              fetchpriority="high"
              draggable={false}
            />
          </figure>

          <div className={Classes.thumbs} role="list">
            {gallery.map((src, idx) => (
              <button
                type="button"
                key={idx}
                className={`${Classes.thumbBtn} ${
                  idx === mainIdx ? Classes.thumbActive : ""
                }`}
                aria-label={`Show image ${idx + 1}`}
                onClick={() => setMainIdx(idx)}
              >
                <img src={src} alt="" className={Classes.thumbImg} />
              </button>
            ))}
          </div>
        </div>

        {/* RIGHT — DETAILS */}
        <div className={Classes.infoCol}>
          <h1 className={Classes.title}>
            {product?.productName || product?.name || "Product"}
          </h1>

          <div className={Classes.byline}>
            {product?.brand && (
              <img
                src={`https://dummyimage.com/84x20/ff7f50/ffffff&text=${encodeURIComponent(
                  String(product.brand).toUpperCase().slice(0, 8)
                )}`}
                alt={product.brand}
                width="84"
                height="20"
                draggable={false}
              />
            )}
            <span>by {product?.brand || "—"}</span>
            {source === "internet" && (
              <span className={Classes.bylineBadge}>Internet</span>
            )}
          </div>

          <p className={Classes.lead}>
            {product?.description || product?.subtitle || "—"}
          </p>

          {/* Feature rows */}
          <div
            className={Classes.specTable}
            role="table"
            aria-label="Key specifications"
          >
            <SpecRow
              label="Usage"
              value={product?.usage || product?.__raw?.category}
            />
            <SpecRow label="Dimmable" value={product?.dimmable} />
            <SpecRow
              label="Temperature"
              value={product?.temperature}
              lockable
            />
            <SpecRow
              label="Beam Angle"
              value={product?.beamAngle}
              lockable
            />
            <SpecRow
              label="Size"
              value={product?.size || product?.__raw?.size}
              lockable
            />
            <div className={Classes.row} role="row">
              <div className={Classes.cellLabel} role="cell">
                Manufacturer
              </div>
              <div className={Classes.cellValue} role="cell">
                {source === "internet"
                  ? product?.brand ||
                    product?.__raw?.manufacturer ||
                    "—"
                  : company?.companyName || "—"}
              </div>
            </div>
            <SpecRow
              label="Wattage"
              value={product?.wattage}
              lockable
            />
            <SpecRow
              label="Color Options"
              value={product?.color || product?.__raw?.color}
            />
          </div>

          {/* Actions */}
          <div className={Classes.actions}>
            <button
              className={Classes.primary}
              onClick={handleAddToCollection}
            >
              Add to collection
            </button>

            {source !== "internet" && (
              <>
                {mustUpgrade && source === "local" ? (
                  <LockedCell
                    onClick={goToUpgrade}
                  >
                    Upgrade to Unlock - 3D model
                  </LockedCell>
                ) : (
                  <a
                    className={Classes.ghost}
                    href={product?.files?.model3dUrl || "#"}
                    target="_blank"
                    rel="noreferrer"
                    aria-disabled={!product?.files?.model3dUrl}
                    onClick={(e) => {
                      if (!product?.files?.model3dUrl) e.preventDefault();
                    }}
                  >
                    <img
                      src="/assets/icons/download.png"
                      height={13}
                    />
                    Download 3D model
                  </a>
                )}

                {mustUpgrade && source === "local" ? (
                  <LockedCell
                    onClick={goToUpgrade}
                  >
                    Upgrade to Unlock - IES
                  </LockedCell>
                ) : (
                  <a
                    className={Classes.ghost}
                    href={product?.files?.photometricsUrl || "#"}
                    target="_blank"
                    rel="noreferrer"
                    aria-disabled={!product?.files?.photometricsUrl}
                    onClick={(e) => {
                      if (!product?.files?.photometricsUrl)
                        e.preventDefault();
                    }}
                  >
                    <img
                      src="/assets/icons/download.png"
                      height={13}
                    />
                    Download IES
                  </a>
                )}
              </>
            )}
          </div>

          {/* Tabs */}
          <div className={Classes.tabsWrapper}>
            <div className={Classes.tabs} role="tablist">
              {TABS.map((t) => (
                <button
                  key={t}
                  role="tab"
                  aria-selected={activeTab === t}
                  className={`${Classes.tabBtn} ${
                    activeTab === t ? Classes.tabActive : ""
                  }`}
                  onClick={() => setActiveTab(t)}
                >
                  {t}
                </button>
              ))}
            </div>

            <div
              className={Classes.tabPanel}
              role="tabpanel"
              aria-live="polite"
            >
              {activeTab === "Specifications" && (
                <>
                  {mustUpgrade && source === "local" ? (
                    <LockedCell onClick={goToUpgrade}>
                      Upgrade to Unlock
                    </LockedCell>
                  ) : (
                    <div className={Classes.tabGrid}>
                      <div className={Classes.tabCol}>
                        <h4 className={Classes.tabTitle}>Details</h4>
                        <ul className={Classes.kvList}>
                          {(product?.temperature ||
                            source !== "internet") && (
                            <li>
                              <span>Temperature</span>
                              <b>{product?.temperature || "—"}</b>
                            </li>
                          )}
                          {(product?.beamAngle ||
                            source !== "internet") && (
                            <li>
                              <span>Beam Angle</span>
                              <b>{product?.beamAngle || "—"}</b>
                            </li>
                          )}
                          {(product?.wattage ||
                            source !== "internet") && (
                            <li>
                              <span>Wattage</span>
                              <b>{product?.wattage || "—"}</b>
                            </li>
                          )}
                        </ul>
                      </div>
                      <div className={Classes.tabCol}>
                        <h4 className={Classes.tabTitle}>
                          Specifications
                        </h4>
                        <div
                          className={Classes.paragraph}
                          style={{ whiteSpace: "pre-wrap" }}
                        >
                          {product?.specifications ||
                            product?.__raw?.description ||
                            "—"}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {activeTab === "Manufacturer Info" && (
                <div className={Classes.tabCols2}>
                  {source === "internet" ? (
                    <div>
                      <h4 className={Classes.tabTitle}>
                        {product?.brand ||
                          product?.__raw?.manufacturer ||
                          "Manufacturer"}
                      </h4>
                      <ul className={Classes.infoList}>
                        {product?.__raw?.category && (
                          <li>Category: {product.__raw.category}</li>
                        )}
                        {product?.__raw?.model && (
                          <li>Model: {product.__raw.model}</li>
                        )}
                        {(product?.__raw?.barcode_number ||
                          product?.__raw?.upc ||
                          product?.__raw?.ean) && (
                          <li>
                            Barcode:{" "}
                            {product.__raw.barcode_number ||
                              product.__raw.upc ||
                              product.__raw.ean}
                          </li>
                        )}
                      </ul>
                      {Array.isArray(product?.__raw?.stores) &&
                      product.__raw.stores.length ? (
                        <div
                          className={Classes.paragraph}
                          style={{ marginTop: 8 }}
                        >
                          <b>Stores:</b>{" "}
                          {product.__raw.stores
                            .map((s) => s?.name)
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div>
                      <h4 className={Classes.tabTitle}>
                        {company?.companyName || "Manufacturer"}
                      </h4>
                      <ul className={Classes.infoList}>
                        <li>Website: {company?.websiteURL || "—"}</li>
                        <li>Phone: {company?.phone || "—"}</li>
                        <li>Slug: {company?.slug || "—"}</li>
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "Installation Guide" &&
                source !== "internet" && (
                  <div className={Classes.installWrap}>
                    <>
                      <ol className={Classes.stepList}>
                        <li>
                          Turn OFF the main power before installation.
                        </li>
                        <li>
                          Mount the bracket and secure the fixture using
                          supplied hardware.
                        </li>
                        <li>
                          Connect live/neutral/ground according to
                          diagram.
                        </li>
                        <li>
                          Adjust beam angle and aim; lock the tilt
                          screws.
                        </li>
                        <li>
                          Restore power and perform functional test.
                        </li>
                      </ol>

                      <div className={Classes.downloads}>
                        <a
                          className={Classes.ghostSmall}
                          href={
                            product?.files?.installationGuideUrl ||
                            "#"
                          }
                          target="_blank"
                          rel="noreferrer"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                          >
                            <path d="M5 20h14v-2H5v2zm7-16l-5 5h3v4h4v-4h3l-5-5z" />
                          </svg>
                          PDF – Installation Manual
                        </a>
                        <a
                          className={Classes.ghostSmall}
                          href={
                            product?.files?.photometricsUrl || "#"
                          }
                          target="_blank"
                          rel="noreferrer"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                          >
                            <path d="M5 20h14v-2H5v2zm7-16l-5 5h3v4h4v-4h3l-5-5z" />
                          </svg>
                          IES Photometrics
                        </a>
                        <a
                          className={Classes.ghostSmall}
                          href={
                            product?.files?.model3dUrl || "#"
                          }
                          target="_blank"
                          rel="noreferrer"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                          >
                            <path d="M5 20h14v-2H5v2zm7-16l-5 5h3v4h4v-4h3l-5-5z" />
                          </svg>
                          3D Model
                        </a>
                      </div>
                    </>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
